import React, { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import {
  Box, Button, Chip, CircularProgress, FormControl, InputLabel, Link,
  MenuItem, Select, Snackbar, Switch, TextField, Typography
} from '@material-ui/core'
import MuiAlert from '@material-ui/lab/Alert'
import { KeyboardDatePicker } from '@material-ui/pickers'
import SaveIcon from '@material-ui/icons/Save'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import EventIcon from '@material-ui/icons/Event'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import CompleteCycleDialog from './CompleteCycleDialog'
import SprayScopeSection from './SprayScopeSection'
import SprayProgressMap from '../v3/SprayProgressMap'
import ContactsSection from '../v3/ContactsSection'
import { FieldActivityMiniList } from './FieldActivityPanel'
import WaterAccessFormDialog from '../../WaterAccess/WaterAccessFormDialog'
import EditIcon from '@material-ui/icons/Edit'
import {
  useV4Styles, scheduleMeta, queueStatusMeta, packetMeta, tokens
} from './v4Styles'

const ASPIRE_BASE_URL = process.env.REACT_APP_ASPIRE_BASE_URL || 'https://cloud.youraspire.com'

const getTicketStatusChipStyle = (status = '') => {
  const normalized = String(status || '').toLowerCase()
  if (normalized.includes('open')) return { backgroundColor: '#e8f5e9', color: '#2e7d32' }
  if (normalized.includes('schedule')) return { backgroundColor: '#e3f2fd', color: '#1565c0' }
  if (normalized.includes('complete') || normalized.includes('closed')) return { backgroundColor: '#eceff1', color: '#546e7a' }
  return { backgroundColor: '#f5f5f5', color: '#616161' }
}

const formatDateOnly = (value) => {
  if (!value) return '\u2014'
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) {
      const [, year, month, day] = match
      return new Date(Number(year), Number(month) - 1, Number(day))
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const getHistorySourceChip = (source) => {
  if (source === 'spray_operation') {
    return { label: 'Spray Op', backgroundColor: '#e3f2fd', color: '#1565c0' }
  }
  if (source === 'aspire_work_ticket') {
    return { label: 'Aspire', backgroundColor: '#fff3e0', color: '#e65100' }
  }
  return { label: 'Spray Log', backgroundColor: '#f1f8e9', color: '#558b2f' }
}

const CollapsibleSection = ({ title, defaultOpen = true, action, children, classes, className }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={`${classes.collapsibleSection} ${className || ''}`}>
      <div className={classes.sectionHeader} onClick={() => setOpen(!open)}>
        <Typography className={classes.sectionTitle}>
          {open ? '\u25BE' : '\u25B8'} {title}
        </Typography>
        {action && <div onClick={(e) => e.stopPropagation()}>{action}</div>}
      </div>
      {open && <div className={classes.sectionBody}>{children}</div>}
    </div>
  )
}

const InlineEditField = ({ label, value, placeholder, onChange, onBlurSave, multiline, classes }) => {
  const [editing, setEditing] = useState(false)

  if (editing) {
    return (
      <Box mb={0.5}>
        <Typography variant="caption" style={{ color: tokens.text.muted, fontSize: 10 }}>{label}</Typography>
        <input
          autoFocus
          value={value || ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => { setEditing(false); if (onBlurSave) onBlurSave() }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !multiline) { setEditing(false); if (onBlurSave) onBlurSave() } }}
          className={classes.inlineEditActive}
        />
      </Box>
    )
  }

  return (
    <Box
      mb={0.5}
      onClick={() => setEditing(true)}
      className={classes.inlineEditDisplay}
    >
      <Typography variant="body2" style={{ fontSize: 12 }}>
        <span style={{ color: tokens.text.secondary, fontWeight: 500 }}>{label}:</span>{' '}
        <span style={{ color: value ? tokens.text.primary : '#bbb' }}>
          {value || placeholder || 'Click to add...'}
        </span>
        <span style={{ color: tokens.accent.blue, fontSize: 9, marginLeft: 4 }}>&#x270E;</span>
      </Typography>
    </Box>
  )
}

const missingFieldLabels = {
  site_name: 'Site Name',
  address: 'Address',
  contact: 'Contact',
  map: 'Map',
  blueDyePreference: 'Blue Dye',
  spray_areas: 'Spray Areas'
}

const missingFieldTargets = {
  site_name: 'access_info',
  address: 'access_info',
  contact: 'contacts',
  map: 'spray_scope',
  blueDyePreference: 'queue_controls',
  spray_areas: 'spray_scope'
}

const formatMissingFieldLabel = (field = '') => (
  missingFieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
)

const formatCompactNumber = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return null
  return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, '')
}

const formatChemicalAmount = (chemical = {}) => {
  const actualOz = formatCompactNumber(chemical.actualVolumeUsedOz)
  if (actualOz) return `${actualOz} oz used`
  const actualVolume = formatCompactNumber(chemical.actualVolumeUsed)
  const actualUnit = chemical.unit || chemical.originalUnit || ''
  if (actualVolume) return `${actualVolume}${actualUnit ? ` ${actualUnit}` : ''} used`
  const plannedVolume = formatCompactNumber(chemical.plannedVolume || chemical.originalVolume)
  const plannedUnit = chemical.originalUnit || chemical.unit || ''
  if (plannedVolume) return `${plannedVolume}${plannedUnit ? ` ${plannedUnit}` : ''}`
  return null
}

const formatConcentration = (value) => {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return null
  return `${formatCompactNumber(num)}%/vol`
}

const DetailLoadingState = ({ classes }) => (
  <div className={classes.detailPanel}>
    <div className={classes.detailStateCard}>
      <Box display="flex" alignItems="center" style={{ gap: 10 }} mb={2}>
        <CircularProgress size={18} />
        <Typography variant="body2" style={{ fontWeight: 700, color: tokens.text.secondary }}>Updating queue...</Typography>
      </Box>
      <div className={classes.detailSkeletonLine} style={{ width: '45%', height: 24 }} />
      <div className={classes.detailSkeletonLine} style={{ width: '30%' }} />
      <div className={classes.detailSkeletonFacts}>
        {[0, 1, 2, 3].map(index => (
          <div key={index} className={classes.detailSkeletonFact}>
            <div className={classes.detailSkeletonLine} style={{ width: '60%' }} />
            <div className={classes.detailSkeletonLine} style={{ width: '85%', height: 18 }} />
          </div>
        ))}
      </div>
    </div>
  </div>
)

const DetailEmptyState = ({ classes, hasActiveFilters, onClearFilters }) => (
  <div className={classes.detailPanel}>
    <div className={classes.detailEmptyState}>
      <Typography variant="h6" style={{ fontWeight: 700, color: tokens.text.primary }}>
        {hasActiveFilters ? 'No sites match these filters' : 'Select a site to inspect'}
      </Typography>
      <Typography variant="body2" style={{ color: tokens.text.secondary, maxWidth: 460, marginTop: 8 }}>
        {hasActiveFilters
          ? 'The detail editor is paused until a queue item matches the current search and filters.'
          : 'Choose a site from the queue to review schedule details, Aspire tickets, spray scope, and notes.'}
      </Typography>
      {hasActiveFilters && (
        <Box mt={2}>
          <Button variant="outlined" size="small" onClick={onClearFilters}>Clear Filters</Button>
        </Box>
      )}
    </div>
  </div>
)

const extractDraftFields = (item) => ({
  priorityOverride: item.priorityOverride || 'normal',
  queueStatus: item.queueStatus || 'draft',
  blueDyePreference: item.blueDyePreference || 'unspecified',
  preferredSubDate: item.preferredSubDate ? new Date(item.preferredSubDate).toISOString() : null,
  internalNotes: item.internalNotes || '',
  vendorNotes: item.vendorNotes || ''
})

const extractSiteInfoFields = (item) => ({
  gateCode: item.siteInfo?.gateCode || '',
  directions: item.siteInfo?.directions || '',
  siteNotes: item.siteInfo?.siteNotes || ''
})

const shallowEqual = (a, b) => {
  if (a === b) return true
  if (!a || !b) return false
  const keysA = Object.keys(a)
  if (keysA.length !== Object.keys(b).length) return false
  return keysA.every(key => a[key] === b[key])
}

const SiteDetailPanel = ({ item, loading, hasActiveFilters, onClearFilters, url, saving, onSave, onOverride, onComplete, onIgnore, onDirtyChange }) => {
  const classes = useV4Styles()
  const [draft, setDraft] = useState({})
  const [historyItems, setHistoryItems] = useState([])
  const [manualCompletions, setManualCompletions] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState('')
  const [fieldActivityItems, setFieldActivityItems] = useState([])
  const [fieldActivityLoading, setFieldActivityLoading] = useState(false)

  const [siteInfo, setSiteInfo] = useState({ gateCode: '', directions: '', siteNotes: '' })
  const [siteInfoDirty, setSiteInfoDirty] = useState(false)
  const [siteInfoSaving, setSiteInfoSaving] = useState(false)
  const [completeCycleOpen, setCompleteCycleOpen] = useState(false)
  const [completeCycleLoading, setCompleteCycleLoading] = useState(false)
  const [completeCycleError, setCompleteCycleError] = useState('')

  // Water access state
  const [waterAccess, setWaterAccess] = useState({ useCustomerWater: false, waterAccessPoint: null, connectionType: '', connectionSize: '', locationNotes: '' })
  const [waterSaving, setWaterSaving] = useState(false)
  const [waterDialogOpen, setWaterDialogOpen] = useState(false)
  const [nearbyPoints, setNearbyPoints] = useState([])
  const [nearbyLoading, setNearbyLoading] = useState(false)
  const [showNearbyPicker, setShowNearbyPicker] = useState(false)

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const sectionRefs = useRef({})

  // Dirty-guard state
  const [isDirty, setIsDirty] = useState(false)
  const [externallyUpdated, setExternallyUpdated] = useState(false)
  const serverSnapshotRef = useRef(null)
  const prevItemIdRef = useRef(null)
  const isDirtyRef = useRef(false)

  const markDirty = useCallback(() => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true
      setIsDirty(true)
      if (onDirtyChange) onDirtyChange(true)
    }
  }, [onDirtyChange])

  const clearDirty = useCallback(() => {
    isDirtyRef.current = false
    setIsDirty(false)
    setExternallyUpdated(false)
    if (onDirtyChange) onDirtyChange(false)
  }, [onDirtyChange])

  const registerSection = useCallback((key, node) => {
    if (node) sectionRefs.current[key] = node
  }, [])

  const jumpToMissingField = useCallback((field) => {
    const target = missingFieldTargets[field] || 'queue_controls'
    const node = sectionRefs.current[target]
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  useEffect(() => {
    if (!item) {
      serverSnapshotRef.current = null
      prevItemIdRef.current = null
      if (isDirtyRef.current) clearDirty()
      return
    }

    const currentSnapshot = extractDraftFields(item)
    const currentSiteInfo = extractSiteInfoFields(item)

    // Different item selected — full reset
    if (item._id !== prevItemIdRef.current) {
      prevItemIdRef.current = item._id
      serverSnapshotRef.current = { draft: currentSnapshot, siteInfo: currentSiteInfo }
      setDraft({
        ...currentSnapshot,
        preferredSubDate: item.preferredSubDate ? new Date(item.preferredSubDate) : null
      })
      setSiteInfo(currentSiteInfo)
      setSiteInfoDirty(false)
      setWaterAccess({
        useCustomerWater: item.waterAccess?.useCustomerWater || false,
        waterAccessPoint: item.waterAccess?.waterAccessPoint || null,
        connectionType: item.waterAccess?.connectionType || '',
        connectionSize: item.waterAccess?.connectionSize || item.waterAccess?.waterAccessPoint?.connectionSize || '',
        locationNotes: item.waterAccess?.locationNotes || ''
      })
      clearDirty()
      return
    }

    // Same item — check dirty guard
    if (!isDirtyRef.current) {
      // Not dirty, apply update normally
      serverSnapshotRef.current = { draft: currentSnapshot, siteInfo: currentSiteInfo }
      setDraft({
        ...currentSnapshot,
        preferredSubDate: item.preferredSubDate ? new Date(item.preferredSubDate) : null
      })
      setSiteInfo(currentSiteInfo)
      setSiteInfoDirty(false)
      setWaterAccess({
        useCustomerWater: item.waterAccess?.useCustomerWater || false,
        waterAccessPoint: item.waterAccess?.waterAccessPoint || null,
        connectionType: item.waterAccess?.connectionType || '',
        connectionSize: item.waterAccess?.connectionSize || item.waterAccess?.waterAccessPoint?.connectionSize || '',
        locationNotes: item.waterAccess?.locationNotes || ''
      })
      return
    }

    // Dirty — check if server data actually changed
    const prevSnapshot = serverSnapshotRef.current
    const serverDraftChanged = !shallowEqual(prevSnapshot?.draft, currentSnapshot)
    const serverSiteInfoChanged = !shallowEqual(prevSnapshot?.siteInfo, currentSiteInfo)

    if (serverDraftChanged || serverSiteInfoChanged) {
      // Server changed externally — show banner but preserve user edits
      serverSnapshotRef.current = { draft: currentSnapshot, siteInfo: currentSiteInfo }
      setExternallyUpdated(true)
    }
    // If server unchanged, skip — preserve user's draft
  }, [item, clearDirty])

  const handleDiscardEdits = useCallback(() => {
    const snapshot = serverSnapshotRef.current
    if (snapshot) {
      setDraft({
        ...snapshot.draft,
        preferredSubDate: snapshot.draft.preferredSubDate ? new Date(snapshot.draft.preferredSubDate) : null
      })
      setSiteInfo(snapshot.siteInfo)
      setSiteInfoDirty(false)
    }
    clearDirty()
  }, [clearDirty])

  const handleSaveSiteInfo = useCallback(async () => {
    if (!item) return
    setSiteInfoSaving(true)
    try {
      await axios.put(`${url}/api/herbicide-schedule/${item._id}/site-info`, siteInfo)
      setSiteInfoDirty(false)
      // Update site info snapshot after successful save
      if (serverSnapshotRef.current) {
        serverSnapshotRef.current = { ...serverSnapshotRef.current, siteInfo: { ...siteInfo } }
      }
      setSnackbar({ open: true, message: 'Site info saved', severity: 'success' })
    } catch (err) {
      console.error('Error saving site info:', err)
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' })
    }
    setSiteInfoSaving(false)
  }, [item, url, siteInfo])

  const handleCompleteCycle = useCallback(async (scopeAction) => {
    if (!item) return
    setCompleteCycleLoading(true)
    setCompleteCycleError('')
    try {
      await axios.put(`${url}/api/herbicide-schedule/${item._id}/complete-cycle`, { scopeAction })
      setCompleteCycleOpen(false)
      if (onComplete) onComplete(item, true)
    } catch (err) {
      console.error('Error completing cycle:', err)
      setCompleteCycleError(err.response?.data?.error || 'Failed to complete cycle. Please try again.')
    }
    setCompleteCycleLoading(false)
  }, [item, url, onComplete])

  useEffect(() => {
    if (!item?._id) {
      setHistoryItems([])
      setManualCompletions([])
      setHistoryLoading(false)
      setHistoryError('')
      return
    }

    const controller = new AbortController()

    const fetchHistory = async () => {
      setHistoryLoading(true)
      setHistoryError('')
      setHistoryItems([])
      setManualCompletions(item.completions || [])

      try {
        const resp = await axios.get(`${url}/api/herbicide-schedule/${item._id}/history`, {
          signal: controller.signal
        })
        setHistoryItems(resp.data?.history || [])
        setManualCompletions(resp.data?.manualCompletions || item.completions || [])
      } catch (err) {
        if (axios.isCancel?.(err) || err.name === 'CanceledError') return
        console.error('Error fetching spray history:', err)
        setHistoryError('Unable to load spray history.')
        setHistoryItems([])
        setManualCompletions(item.completions || [])
      } finally {
        if (!controller.signal.aborted) setHistoryLoading(false)
      }
    }

    fetchHistory()

    return () => {
      controller.abort()
    }
  }, [item?._id, url])

  useEffect(() => {
    if (!item?._id) {
      setFieldActivityItems([])
      setFieldActivityLoading(false)
      return
    }

    const controller = new AbortController()

    const fetchFieldActivity = async () => {
      setFieldActivityLoading(true)
      try {
        const resp = await axios.get(`${url}/api/herbicide-schedule/${item._id}/activity`, {
          params: { limit: 8 },
          signal: controller.signal
        })
        setFieldActivityItems(resp.data?.items || [])
      } catch (err) {
        if (axios.isCancel?.(err) || err.name === 'CanceledError') return
        console.error('Error fetching field activity:', err)
        setFieldActivityItems([])
      } finally {
        if (!controller.signal.aborted) setFieldActivityLoading(false)
      }
    }

    fetchFieldActivity()

    return () => {
      controller.abort()
    }
  }, [item?._id, url])

  const handleSaveWaterAccess = useCallback(async (updates) => {
    if (!item) return
    const payload = { ...waterAccess, ...updates }
    setWaterAccess(payload)
    setWaterSaving(true)
    try {
      const resp = await axios.put(`${url}/api/herbicide-schedule/${item._id}/water-access`, {
        useCustomerWater: payload.useCustomerWater,
        waterAccessPointId: payload.waterAccessPoint?._id || payload.waterAccessPoint,
        connectionType: payload.connectionType,
        connectionSize: payload.connectionSize,
        locationNotes: payload.locationNotes
      })
      if (resp.data?.waterAccessPoint) {
        setWaterAccess(prev => ({ ...prev, waterAccessPoint: resp.data.waterAccessPoint }))
      }
    } catch (err) {
      console.error('Error saving water access:', err)
    }
    setWaterSaving(false)
  }, [item, url, waterAccess])

  const handleAutoCreate = useCallback(async (opts = {}) => {
    if (!item) return
    setWaterSaving(true)
    try {
      const resp = await axios.post(`${url}/api/herbicide-schedule/${item._id}/water-access/auto-create`, {
        connectionType: opts.connectionType || '',
        connectionSize: opts.connectionSize || '',
        locationNotes: opts.locationNotes || '',
        availabilityScope: opts.availabilityScope || 'job_only'
      })
      const { waterAccess: wa, waterAccessPoint: wap } = resp.data
      setWaterAccess({
        useCustomerWater: true,
        waterAccessPoint: wap,
        connectionType: wa.connectionType,
        connectionSize: wa.connectionSize,
        locationNotes: wa.locationNotes
      })
    } catch (err) {
      console.error('Error auto-creating water access point:', err)
    }
    setWaterSaving(false)
  }, [item, url])

  const handleToggleWater = useCallback(async (enabled) => {
    if (!enabled) {
      handleSaveWaterAccess({ useCustomerWater: false })
      return
    }
    // Check for nearby existing water access points
    setNearbyLoading(true)
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/${item._id}/water-access/nearby`)
      const points = resp.data?.nearbyPoints || []
      if (points.length > 0) {
        setNearbyPoints(points)
        setShowNearbyPicker(true)
        setNearbyLoading(false)
        return
      }
    } catch (err) {
      console.error('Error checking nearby water access:', err)
    }
    setNearbyLoading(false)
    handleAutoCreate({})
  }, [item, url, handleSaveWaterAccess, handleAutoCreate])

  const handleLinkExisting = useCallback(async (existingPoint) => {
    setShowNearbyPicker(false)
    setWaterSaving(true)
    try {
      await axios.put(`${url}/api/herbicide-schedule/${item._id}/water-access`, {
        useCustomerWater: true,
        waterAccessPointId: existingPoint._id,
        connectionType: existingPoint.connectionType || '',
        connectionSize: existingPoint.connectionSize || '',
        locationNotes: ''
      })
      setWaterAccess({
        useCustomerWater: true,
        waterAccessPoint: existingPoint,
        connectionType: existingPoint.connectionType || '',
        connectionSize: existingPoint.connectionSize || '',
        locationNotes: ''
      })
    } catch (err) {
      console.error('Error linking existing water access point:', err)
    }
    setWaterSaving(false)
  }, [item, url])

  const handleWaterDialogSave = useCallback(async (payload, existingId) => {
    const base = `${url}/api/water-access`
    if (existingId) {
      const resp = await axios.put(`${base}/${existingId}`, payload)
      const updated = resp.data?.data || resp.data
      setWaterAccess(prev => ({
        ...prev,
        waterAccessPoint: updated,
        connectionType: updated?.connectionType || prev.connectionType,
        connectionSize: updated?.connectionSize || prev.connectionSize
      }))
    } else {
      const resp = await axios.post(base, payload)
      const created = resp.data?.data || resp.data
      await axios.put(`${url}/api/herbicide-schedule/${item._id}/water-access`, {
        useCustomerWater: true,
        waterAccessPointId: created._id,
        connectionType: created.connectionType || '',
        connectionSize: created.connectionSize || '',
        locationNotes: ''
      })
      setWaterAccess({
        useCustomerWater: true,
        waterAccessPoint: created,
        connectionType: created.connectionType || '',
        connectionSize: created.connectionSize || '',
        locationNotes: ''
      })
    }
  }, [item, url])

  if (loading) return <DetailLoadingState classes={classes} />

  if (!item) return <DetailEmptyState classes={classes} hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />

  const status = scheduleMeta[item.status] || scheduleMeta.ok
  const sprayProgress = item.sprayProgress || {}
  const progressMeta = queueStatusMeta[sprayProgress.status] || queueStatusMeta[item.queueStatus] || queueStatusMeta.draft
  const effectiveNext = item.overriddenNextDate || item.nextScheduledDate
  const lastAppliedTicket = item.lastAppliedTicket || null
  const futureTicket = (item.pendingTickets || [])
    .filter(t => t.statusId !== 7 && t.scheduledDate && new Date(t.scheduledDate) >= new Date())
    .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))[0] || null

  const formatDate = (d) => {
    if (!d) return '\u2014'
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatLoadSummary = (load = {}) => {
    const parts = []
    if (load.completedAreaCount > 0) {
      parts.push(`${load.completedAreaCount} whole area${load.completedAreaCount === 1 ? '' : 's'} done`)
    }
    if (formatCompactNumber(load.completedSplitAreaSF)) {
      parts.push(`${formatCompactNumber(load.completedSplitAreaSF)} SF split done`)
    }
    if (!parts.length) {
      parts.push(load.reportedAt ? 'Completed area not itemized' : 'No completed area reported')
    }
    if (load.remainingAreaCount) parts.push(`${load.remainingAreaCount} remaining`)
    if (load.remainingSplitAreaCount) parts.push(`${load.remainingSplitAreaCount} split remaining`)
    if (formatCompactNumber(load.usedGallons)) parts.push(`${formatCompactNumber(load.usedGallons)} gal used`)
    return parts.join(' \u00B7 ')
  }

  const handleSave = async () => {
    try {
      await onSave(item._id, draft)
      // Update snapshot to match what we just saved, clear dirty
      serverSnapshotRef.current = {
        draft: extractDraftFields({ ...item, ...draft, preferredSubDate: draft.preferredSubDate ? new Date(draft.preferredSubDate).toISOString() : null }),
        siteInfo: serverSnapshotRef.current?.siteInfo || extractSiteInfoFields(item)
      }
      clearDirty()
      setSnackbar({ open: true, message: 'Saved', severity: 'success' })
    } catch (err) {
      const serverMsg = err?.response?.data?.error
      setSnackbar({ open: true, message: serverMsg || 'Save failed', severity: 'warning' })
    }
  }

  return (
    <div className={classes.detailPanel}>
      {/* Site Header */}
      <div className={classes.detailHeader}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <div>
            <Typography variant="h6" style={{ fontWeight: 700, color: tokens.text.primary }}>
              {item.siteName || '(unnamed)'}
            </Typography>
            <Typography variant="body2" style={{ color: tokens.text.secondary, marginTop: 2 }}>
              {item.aspirePropertyId ? (
                <Link
                  href={`${ASPIRE_BASE_URL}/app/properties/overview/${item.aspirePropertyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#6f8791', fontWeight: 500 }}
                >
                  {item.address || 'Aspire property'} (Aspire Property ID: {item.aspirePropertyId})
                </Link>
              ) : (
                item.address || 'No address'
              )}
            </Typography>
          </div>
          <Box display="flex" style={{ gap: 6 }} alignItems="center">
            {item.needsAspireCompletion && (
              <Chip
                size="small"
                label="Complete Aspire Ticket"
                style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700 }}
              />
            )}
            <Chip
              size="small"
              label={`${status.label}${item.daysUntilNext != null ? ` \u2014 ${Math.abs(item.daysUntilNext)}d${item.daysUntilNext < 0 ? ' past' : ''}` : ''}`}
              style={{ backgroundColor: `${status.color}15`, color: status.color, fontWeight: 600 }}
            />
          </Box>
        </Box>
        <Typography variant="caption" style={{ color: tokens.text.hint, marginTop: 4, display: 'block' }}>
          {item.communityName || ''} {item.buildingNumber ? `\u00B7 Bldg ${item.buildingNumber}` : ''}
          {item.herbicideTerritory?.name ? ` \u00B7 ${item.herbicideTerritory.name}` : ''}
        </Typography>
      </div>

      {/* Externally updated banner */}
      {externallyUpdated && (
        <Box
          display="flex" justifyContent="space-between" alignItems="center"
          style={{ background: '#fff3e0', borderLeft: '3px solid #ff9800', padding: '6px 12px', margin: '0 0 8px 0', borderRadius: 4 }}
        >
          <Typography variant="caption" style={{ color: '#e65100', fontWeight: 600 }}>
            This item was updated externally. Your unsaved edits are preserved.
          </Typography>
          <Box display="flex" style={{ gap: 4 }} alignItems="center">
            <Button size="small" onClick={handleDiscardEdits} style={{ color: '#e65100', fontSize: 11, fontWeight: 600, minWidth: 0 }}>
              Reload Server Data
            </Button>
            <Typography
              variant="caption"
              onClick={() => setExternallyUpdated(false)}
              style={{ color: '#e65100', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 4px' }}
            >
              &times;
            </Typography>
          </Box>
        </Box>
      )}

      {/* Quick Facts — elevated card */}
      <div className={classes.quickFactsCard}>
        <div className={classes.quickFactItem}>
          <Typography variant="caption" style={{ color: tokens.text.muted, textTransform: 'uppercase', fontSize: 10 }}>Last Applied</Typography>
          {lastAppliedTicket?.workTicketId && (
            <Box className={classes.quickFactTicketValue}>
              <Link
                href={`${ASPIRE_BASE_URL}/app/worktickets/details/${lastAppliedTicket.workTicketId}`}
                target="_blank" rel="noopener noreferrer"
                className={classes.quickFactTicketLink}
              >
                {formatDateOnly(item.lastAppliedDate)}
              </Link>
              {lastAppliedTicket.status && (
                <Chip
                  size="small"
                  label={lastAppliedTicket.status}
                  style={{
                    ...getTicketStatusChipStyle(lastAppliedTicket.status),
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700
                  }}
                />
              )}
            </Box>
          )}
          {!lastAppliedTicket?.workTicketId && (
            <Typography variant="body2" style={{ fontWeight: 600 }}>{formatDateOnly(item.lastAppliedDate)}</Typography>
          )}
        </div>
        <div className={classes.quickFactItem}>
          <Typography variant="caption" style={{ color: tokens.text.muted, textTransform: 'uppercase', fontSize: 10 }}>Next Due</Typography>
          <Typography variant="body2" style={{ fontWeight: 600, color: item.daysUntilNext < 0 ? '#c62828' : tokens.text.primary }}>
            {formatDateOnly(effectiveNext)}
          </Typography>
        </div>
        <div className={classes.quickFactItem}>
          <Typography variant="caption" style={{ color: tokens.text.muted, textTransform: 'uppercase', fontSize: 10 }}>Blue Dye</Typography>
          <Typography variant="body2" style={{ fontWeight: 600, color: item.blueDyePreference === 'yes' ? '#1976d2' : tokens.text.secondary }}>
            {item.blueDyePreference === 'yes' ? 'Yes' : item.blueDyePreference === 'no' ? 'No' : 'Unset'}
          </Typography>
        </div>
        <div className={classes.quickFactItem}>
          <Typography variant="caption" style={{ color: tokens.text.muted, textTransform: 'uppercase', fontSize: 10, display: 'block' }}>Aspire Ticket</Typography>
          {futureTicket ? (
            <Box className={classes.quickFactTicketValue}>
              <Link
                href={`${ASPIRE_BASE_URL}/app/worktickets/details/${futureTicket.workTicketId}`}
                target="_blank" rel="noopener noreferrer"
                className={classes.quickFactTicketLink}
              >
                {formatDateOnly(futureTicket.scheduledDate)}
              </Link>
              <Chip
                size="small"
                label={futureTicket.status || `Status ${futureTicket.statusId}`}
                style={{
                  ...getTicketStatusChipStyle(futureTicket.status),
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" style={{ fontWeight: 600, color: tokens.text.muted }}>{'\u2014'}</Typography>
          )}
        </div>
      </div>

      {/* Actions — toned down */}
      <div className={classes.actionBar}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<CheckCircleIcon />}
          onClick={() => onComplete(item)}
        >
          Mark Complete
        </Button>
        {['submitted', 'partial', 'in-progress'].includes(item.queueStatus) && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => setCompleteCycleOpen(true)}
            disabled={completeCycleLoading}
          >
            Complete Cycle
          </Button>
        )}
        <Button variant="outlined" size="small" startIcon={<EventIcon />} onClick={() => onOverride(item)}>
          Override Date
        </Button>
        <Button
          variant="text"
          size="small"
          startIcon={<VisibilityOffIcon />}
          onClick={() => onIgnore(item)}
          style={{ color: tokens.text.muted }}
        >
          {item.ignored ? 'Unignore' : 'Ignore'}
        </Button>
      </div>

      <CompleteCycleDialog
        open={completeCycleOpen}
        loading={completeCycleLoading}
        error={completeCycleError}
        onClose={() => setCompleteCycleOpen(false)}
        onComplete={handleCompleteCycle}
      />

      {/* Spray Progress */}
      {sprayProgress.operationCount > 0 && (
        <CollapsibleSection
          title={`Spray Progress (${sprayProgress.percentComplete || 0}%)`}
          defaultOpen
          classes={classes}
          className={classes.sectionGroup}
          action={
            <Chip
              size="small"
              label={progressMeta.label}
              style={{
                backgroundColor: progressMeta.color,
                color: progressMeta.textColor,
                fontWeight: 600
              }}
            />
          }
        >
          <Box display="flex" style={{ gap: 12 }} flexWrap="wrap" mb={1}>
            <Typography variant="caption" style={{ color: '#455a64' }}>
              {sprayProgress.completedAreaCount || 0}/{sprayProgress.plannedAreaCount || 0} scoped areas complete
            </Typography>
            <Typography variant="caption" style={{ color: '#455a64' }}>
              {sprayProgress.remainingAreaCount || 0} remaining
            </Typography>
            {formatCompactNumber(sprayProgress.completedAreaSF) && formatCompactNumber(sprayProgress.plannedAreaSF) && (
              <Typography variant="caption" style={{ color: '#455a64' }}>
                {formatCompactNumber(sprayProgress.completedAreaSF)} / {formatCompactNumber(sprayProgress.plannedAreaSF)} SF
              </Typography>
            )}
            <Typography variant="caption" style={{ color: '#455a64' }}>
              {sprayProgress.operationCount || 0} load{sprayProgress.operationCount === 1 ? '' : 's'}
            </Typography>
            {formatCompactNumber(sprayProgress.totalUsedGallons) && (
              <Typography variant="caption" style={{ color: '#455a64' }}>
                {formatCompactNumber(sprayProgress.totalUsedGallons)} gal used
              </Typography>
            )}
          </Box>
          <Box mb={1.5}>
            <SprayProgressMap
              url={url}
              scheduleItemId={item._id}
              jobsiteId={item.jobsite?._id || item.jobsite}
              sprayScopeResolution={item.sprayScopeResolution}
              sprayProgress={sprayProgress}
            />
          </Box>
          {(sprayProgress.loadHistory || []).map((load, i) => (
            <Box key={load.operationId || i} display="flex" justifyContent="space-between" alignItems="flex-start" style={{ padding: '6px 0', borderBottom: '1px solid #f1f3f4', gap: 8 }}>
              <div>
                <Typography variant="body2" style={{ fontSize: 12, fontWeight: 600 }}>
                  Load {load.loadNumber || i + 1}
                </Typography>
                <Typography variant="caption" style={{ color: tokens.text.secondary }}>
                  {formatLoadSummary(load)}
                </Typography>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Chip
                  size="small"
                  label={queueStatusMeta[load.status]?.label || load.status || 'Load'}
                  style={{
                    backgroundColor: queueStatusMeta[load.status]?.color || '#eceff1',
                    color: queueStatusMeta[load.status]?.textColor || '#455a64',
                    fontWeight: 600
                  }}
                />
                {load.eventDate && (
                  <Typography variant="caption" style={{ color: tokens.text.muted, display: 'block', marginTop: 3 }}>
                    {formatDate(load.eventDate)}
                  </Typography>
                )}
              </div>
            </Box>
          ))}
        </CollapsibleSection>
      )}

      {/* Queue Controls */}
      <div ref={(node) => registerSection('queue_controls', node)}>
        <CollapsibleSection title={`Queue Controls${isDirty ? ' (unsaved)' : ''}`} classes={classes} className={classes.sectionGroup} action={
          <Button
            size="small"
            color="primary"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            style={{ fontSize: 11, backgroundColor: isDirty ? '#ff9800' : undefined }}
          >
            Save
          </Button>
        }>
        <Box display="flex" style={{ gap: 12 }} flexWrap="wrap">
          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select value={draft.priorityOverride || 'normal'} onChange={(e) => { setDraft({ ...draft, priorityOverride: e.target.value }); markDirty() }} label="Priority">
              <MenuItem value="rush">Rush</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
            <InputLabel>Queue Status</InputLabel>
            <Select value={draft.queueStatus || 'draft'} onChange={(e) => { setDraft({ ...draft, queueStatus: e.target.value }); markDirty() }} label="Queue Status">
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
              <MenuItem value="submitted">Needs Review</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
            <InputLabel>Blue Dye</InputLabel>
            <Select value={draft.blueDyePreference || 'unspecified'} onChange={(e) => { setDraft({ ...draft, blueDyePreference: e.target.value }); markDirty() }} label="Blue Dye">
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="unspecified">Unspecified</MenuItem>
            </Select>
          </FormControl>
          <KeyboardDatePicker
            label="Target Date"
            format="MM/dd/yyyy"
            value={draft.preferredSubDate}
            onChange={(date) => { setDraft({ ...draft, preferredSubDate: date }); markDirty() }}
            inputVariant="outlined"
            size="small"
            clearable
            style={{ maxWidth: 160 }}
          />
        </Box>
        {item.packetStatus && (
          <Box mt={1}>
            <Chip
              size="small"
              label={packetMeta[item.packetStatus]?.label || item.packetStatus}
              style={{
                backgroundColor: packetMeta[item.packetStatus]?.color || '#ccc',
                color: packetMeta[item.packetStatus]?.textColor || '#fff',
                marginRight: 4
              }}
            />
            {item.packetMissingFields?.map(field => (
              <Chip
                key={field}
                size="small"
                label={`Missing: ${formatMissingFieldLabel(field)}`}
                onClick={() => jumpToMissingField(field)}
                style={{
                  marginRight: 4,
                  marginTop: 4,
                  cursor: 'pointer',
                  backgroundColor: '#fff8e1',
                  color: '#e65100',
                  fontWeight: 600
                }}
              />
            ))}
          </Box>
        )}
        </CollapsibleSection>
      </div>

      {/* Spray Scope */}
      <div ref={(node) => registerSection('spray_scope', node)}>
        <CollapsibleSection
          title={`Spray Scope${item.areaSummary?.areaCount ? ` (${item.areaSummary.areaCount} areas)` : ''}`}
          classes={classes}
          className={classes.sectionGroup}
          action={
            <Typography variant="caption" style={{ color: item.sprayScope?.mode === 'all' ? '#2e7d32' : tokens.accent.blue, fontWeight: 600 }}>
              {item.sprayScope?.mode === 'all' ? 'All Areas' : `${(item.sprayScope?.selectedAreaIds || []).length} selected`}
              {item.sprayScope?.totalAcres ? ` \u00B7 ${item.sprayScope.totalAcres} ac` : ''}
            </Typography>
          }
        >
          <SprayScopeSection
            url={url}
            scheduleItemId={item._id}
            jobsiteId={item.jobsite?._id || item.jobsite}
            jobsiteLocation={item.jobsite?.location || null}
            sprayScope={item.sprayScope}
          />
        </CollapsibleSection>
      </div>

      {/* Notes */}
      <CollapsibleSection title="Notes" classes={classes} className={classes.sectionGroup}>
        <TextField
          label="Internal Notes (your team)"
          value={draft.internalNotes || ''}
          onChange={(e) => { setDraft({ ...draft, internalNotes: e.target.value }); markDirty() }}
          variant="outlined"
          fullWidth
          multiline
          rows={2}
          size="small"
          style={{ marginBottom: 8 }}
        />
        <TextField
          label="Vendor Notes (visible to sub)"
          value={draft.vendorNotes || ''}
          onChange={(e) => { setDraft({ ...draft, vendorNotes: e.target.value }); markDirty() }}
          variant="outlined"
          fullWidth
          multiline
          rows={2}
          size="small"
        />
      </CollapsibleSection>

      {/* Contacts */}
      <div ref={(node) => registerSection('contacts', node)}>
        <CollapsibleSection title={`Contacts`} classes={classes} className={classes.sectionGroup}>
          <ContactsSection
            url={url}
            scheduleItemId={item._id}
            preferredContact={item.preferredContact}
            visibleContacts={item.visibleContacts}
            clientId={item.jobsite?.client?._id || item.jobsite?.client}
          />
        </CollapsibleSection>
      </div>

      {/* Water Access */}
      <CollapsibleSection
        title="Water Access"
        classes={classes}
        className={classes.sectionGroup}
        action={
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Typography variant="caption" style={{ color: waterAccess.useCustomerWater ? '#2e7d32' : tokens.text.muted, fontWeight: 600, fontSize: 11 }}>
              {waterAccess.useCustomerWater ? 'Use Their Water' : 'Off'}
            </Typography>
            {nearbyLoading && <CircularProgress size={12} />}
            <Switch
              size="small"
              checked={waterAccess.useCustomerWater}
              onChange={(e) => handleToggleWater(e.target.checked)}
              color="primary"
              disabled={nearbyLoading}
            />
          </Box>
        }
      >
        {/* Nearby picker — shown when toggle ON finds existing points */}
        {showNearbyPicker && (
          <Box mb={1} p={1} style={{ background: '#f5f5f0', borderRadius: 6, border: '1px solid #ddd' }}>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#20342b' }}>
              Found {nearbyPoints.length} nearby water source{nearbyPoints.length > 1 ? 's' : ''}:
            </Typography>
            {nearbyPoints.map((pt, idx) => (
              <Box key={pt._id || idx} display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                <Typography variant="body2" style={{ fontSize: 12 }}>
                  {pt.name} ({(pt.connectionType || '').replace(/_/g, ' ')}) — {Math.round(pt.distanceMeters)}m
                </Typography>
                <Button size="small" variant="outlined" onClick={() => handleLinkExisting(pt)} style={{ fontSize: 10, minWidth: 50, padding: '2px 8px' }}>
                  Link
                </Button>
              </Box>
            ))}
            <Box mt={1} display="flex" style={{ gap: 8 }}>
              <Button size="small" onClick={() => { setShowNearbyPicker(false); handleAutoCreate({}) }} style={{ fontSize: 11, textTransform: 'none' }}>
                Create New Instead
              </Button>
              <Button size="small" onClick={() => setShowNearbyPicker(false)} style={{ fontSize: 11, textTransform: 'none' }}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {waterAccess.useCustomerWater ? (
          <Box>
            {/* Compact summary of linked WaterAccessPoint */}
            {waterAccess.waterAccessPoint && typeof waterAccess.waterAccessPoint === 'object' && (
              <Box mb={1} p={1} style={{ background: '#e8f5e9', borderRadius: 6 }}>
                <Typography variant="body2" style={{ fontWeight: 600, fontSize: 12, color: '#2e7d32' }}>
                  {waterAccess.waterAccessPoint.name || 'Water Source'}
                </Typography>
                <Typography variant="caption" style={{ color: '#555', display: 'block' }}>
                  {(waterAccess.waterAccessPoint.connectionType || '').replace(/_/g, ' ')}
                  {waterAccess.waterAccessPoint.connectionSize ? ` (${waterAccess.waterAccessPoint.connectionSize})` : ''}
                  {waterAccess.waterAccessPoint.address?.formatted ? ` — ${waterAccess.waterAccessPoint.address.formatted}` : ''}
                </Typography>
                {waterAccess.waterAccessPoint.availabilityScope && waterAccess.waterAccessPoint.availabilityScope !== 'job_only' && (
                  <Chip size="small" label={waterAccess.waterAccessPoint.availabilityScope.replace(/_/g, ' ')} style={{ fontSize: 10, height: 18, marginTop: 4 }} />
                )}
              </Box>
            )}

            {/* Inline quick-edit fields */}
            <Box display="flex" style={{ gap: 10, marginBottom: 8 }} flexWrap="wrap">
              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <InputLabel>Connection</InputLabel>
                <Select
                  value={waterAccess.connectionType || ''}
                  onChange={(e) => handleSaveWaterAccess({ connectionType: e.target.value })}
                  label="Connection"
                >
                  <MenuItem value="">Not Set</MenuItem>
                  <MenuItem value="hose_bib">Hose Bib</MenuItem>
                  <MenuItem value="spigot">Spigot</MenuItem>
                  <MenuItem value="hydrant">Hydrant</MenuItem>
                  <MenuItem value="quick_coupler">Quick Coupler</MenuItem>
                  <MenuItem value="overhead_dump">Overhead Dump</MenuItem>
                  <MenuItem value="pond_lake">Pond / Lake</MenuItem>
                  <MenuItem value="tank_cistern">Tank / Cistern</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" style={{ minWidth: 80 }}>
                <InputLabel>Size</InputLabel>
                <Select
                  value={waterAccess.connectionSize || ''}
                  onChange={(e) => handleSaveWaterAccess({ connectionSize: e.target.value })}
                  label="Size"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  <MenuItem value="1/2&quot;">1/2"</MenuItem>
                  <MenuItem value="3/4&quot;">3/4"</MenuItem>
                  <MenuItem value="1&quot;">1"</MenuItem>
                  <MenuItem value="1 1/2&quot;">1 1/2"</MenuItem>
                  <MenuItem value="2&quot;">2"</MenuItem>
                </Select>
              </FormControl>
              {waterSaving && <CircularProgress size={14} style={{ alignSelf: 'center' }} />}
            </Box>
            <TextField
              label="Location Notes (where to find it, hose length, etc.)"
              value={waterAccess.locationNotes || ''}
              onChange={(e) => setWaterAccess(prev => ({ ...prev, locationNotes: e.target.value }))}
              onBlur={() => handleSaveWaterAccess({ locationNotes: waterAccess.locationNotes })}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              size="small"
            />

            {/* Edit Full Details button */}
            <Box mt={1} display="flex" justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon style={{ fontSize: 14 }} />}
                onClick={() => setWaterDialogOpen(true)}
                style={{ fontSize: 11, textTransform: 'none' }}
              >
                Edit Full Details
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" style={{ color: tokens.text.muted, fontSize: 12 }}>
            No water access set. Toggle on if crew can use customer's water.
          </Typography>
        )}
      </CollapsibleSection>

      {/* Water Access Full-Edit Dialog */}
      <WaterAccessFormDialog
        open={waterDialogOpen}
        onClose={() => setWaterDialogOpen(false)}
        onSave={handleWaterDialogSave}
        editItem={waterAccess.waterAccessPoint && typeof waterAccess.waterAccessPoint === 'object' ? waterAccess.waterAccessPoint : null}
        apiBase={`${url}/api/water-access`}
      />

      {/* Access Info */}
      <div ref={(node) => registerSection('access_info', node)}>
        <CollapsibleSection title="Access Info" classes={classes} className={classes.sectionGroup}>
          <InlineEditField
            label="Gate Code"
            value={siteInfo.gateCode}
            placeholder="Add gate code..."
            onChange={(val) => { setSiteInfo({ ...siteInfo, gateCode: val }); setSiteInfoDirty(true); markDirty() }}
            onBlurSave={handleSaveSiteInfo}
            classes={classes}
          />
          <InlineEditField
            label="Directions"
            value={siteInfo.directions}
            placeholder="Add directions..."
            onChange={(val) => { setSiteInfo({ ...siteInfo, directions: val }); setSiteInfoDirty(true); markDirty() }}
            onBlurSave={handleSaveSiteInfo}
            multiline
            classes={classes}
          />
          <InlineEditField
            label="Site Notes"
            value={siteInfo.siteNotes}
            placeholder="Add site notes..."
            onChange={(val) => { setSiteInfo({ ...siteInfo, siteNotes: val }); setSiteInfoDirty(true); markDirty() }}
            onBlurSave={handleSaveSiteInfo}
            multiline
            classes={classes}
          />
          <Link
            href={`/site-takeoff-v2/${item.jobsite?._id || item.jobsite}`}
            target="_blank"
            style={{ fontSize: 11, color: tokens.accent.blue }}
          >
            To add restricted areas, open SiteTakeoff &rarr;
          </Link>
        </CollapsibleSection>
      </div>

      {/* Spray History */}
      <CollapsibleSection title={`Spray History (${historyItems.length || manualCompletions.length || 0})`} defaultOpen={false} classes={classes} className={classes.sectionGroup}>
        {historyLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={18} />
          </Box>
        ) : historyItems.length > 0 ? (
          historyItems.map((entry, i) => {
            const sourceChip = getHistorySourceChip(entry.source)
            const summaryBits = [
              entry.createdByName,
              formatCompactNumber(entry.areaAcres) ? `${formatCompactNumber(entry.areaAcres)} ac` : null,
              formatCompactNumber(entry.waterUsed) ? `${formatCompactNumber(entry.waterUsed)} gal water` : null
            ].filter(Boolean)

            return (
              <Box key={`${entry.source}-${entry._id || entry.operationId || i}`} style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f4' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 8 }}>
                  <div>
                    <Typography variant="body2" style={{ fontSize: 12, fontWeight: 600 }}>
                      {formatDate(entry.eventDate)}
                    </Typography>
                    {summaryBits.length > 0 && (
                      <Typography variant="caption" style={{ color: tokens.text.secondary }}>
                        {summaryBits.join(' \u00B7 ')}
                      </Typography>
                    )}
                  </div>
                  <Chip
                    size="small"
                    label={sourceChip.label}
                    style={{
                      backgroundColor: sourceChip.backgroundColor,
                      color: sourceChip.color,
                      fontWeight: 600
                    }}
                  />
                </Box>

                {entry.chemicals?.length > 0 ? (
                  <Box mt={1}>
                    {entry.chemicals.map((chemical, index) => {
                      const detailBits = [
                        formatChemicalAmount(chemical),
                        formatConcentration(chemical.concentration)
                      ].filter(Boolean)

                      return (
                        <Box key={`${chemical.chemicalName}-${index}`} mb={0.5}>
                          <Typography variant="body2" style={{ fontSize: 12, fontWeight: 600 }}>
                            {chemical.chemicalName}
                          </Typography>
                          {detailBits.length > 0 && (
                            <Typography variant="caption" style={{ color: tokens.text.secondary }}>
                              {detailBits.join(' \u00B7 ')}
                            </Typography>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                ) : (
                  <Typography variant="caption" style={{ color: tokens.text.secondary, display: 'block', marginTop: 6 }}>
                    No chemical detail on this record.
                  </Typography>
                )}

                {entry.notes && (
                  <Typography variant="caption" style={{ color: '#777', display: 'block', marginTop: 6 }}>
                    {entry.notes}
                  </Typography>
                )}
              </Box>
            )
          })
        ) : manualCompletions.length > 0 ? (
          <>
            {historyError && (
              <Typography variant="caption" color="error" style={{ display: 'block', marginBottom: 8 }}>
                {historyError}
              </Typography>
            )}
            <Typography variant="caption" style={{ color: '#777', display: 'block', marginBottom: 8 }}>
              No spray operation history yet. Showing manual completion records.
            </Typography>
            {manualCompletions.slice().reverse().map((completion, i) => (
              <Box key={i} display="flex" justifyContent="space-between" style={{ padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                <Typography variant="body2" style={{ fontSize: 11 }}>{formatDate(completion.completedDate)}</Typography>
                <Typography variant="body2" style={{ fontSize: 11, color: tokens.text.secondary }}>{completion.source || 'manual'}</Typography>
                <Typography variant="body2" style={{ fontSize: 11, color: tokens.text.muted }}>{completion.notes || '\u2014'}</Typography>
              </Box>
            ))}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
            {historyError || 'No application history found.'}
          </Typography>
        )}

        {historyItems.length > 0 && manualCompletions.length > 0 && (
          <Box mt={1.5}>
            <Typography variant="caption" style={{ color: '#777', display: 'block', marginBottom: 6 }}>
              Legacy completion records
            </Typography>
            {manualCompletions.slice().reverse().map((completion, i) => (
              <Box key={`completion-${i}`} display="flex" justifyContent="space-between" style={{ padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                <Typography variant="body2" style={{ fontSize: 11 }}>{formatDate(completion.completedDate)}</Typography>
                <Typography variant="body2" style={{ fontSize: 11, color: tokens.text.secondary }}>{completion.source || 'manual'}</Typography>
                <Typography variant="body2" style={{ fontSize: 11, color: tokens.text.muted }}>{completion.notes || '\u2014'}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </CollapsibleSection>

      {/* Field Activity (collapsed by default — right panel shows site activity) */}
      <CollapsibleSection
        title={`Field Activity (${fieldActivityItems.length})`}
        defaultOpen={false}
        classes={classes}
        className={classes.sectionGroup}
      >
        <FieldActivityMiniList
          activities={fieldActivityItems}
          loading={fieldActivityLoading}
          emptyText="No subcontractor activity for this site yet."
          compact
        />
      </CollapsibleSection>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' || snackbar.severity === 'warning' ? null : 2000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  )
}

export default SiteDetailPanel
